import React, { useEffect, useMemo, useRef, useState } from "react";

import MotorTable from "../comp/MotorTable";
import SensoryTable from "../comp/SensoryTable";
import FWaveTable from "../comp/FWaveTable";
import HReflexTable from "../comp/HReflexTable";
import EMGPanel from "../comp/EMGPanel";
import FileUploader from "../comp/FileUploader";
import PatientInfo from "../comp/PatientInfo";
import InterpretationPanel from "../comp/InterpretationPanel";
import AutoFillNotes from "../comp/AutoFill_Notes";
import AccountActions from "../comp/AccountActions";
import {
  decodePayload,
  getToken,
  logout,
  startExpiryTimer,
  tokenExpired,
} from "../util/auth";
import { generateFindings } from "../api/ruleEngine";
import {
  extractMotorNerves,
  extractRecSites,
  extractSensoryNerves,
  extractStimSitesFlat,
} from "../util/extractors";
import {
  EMGRowType,
  FWaveRowType,
  HReflexRowType,
  MotorRowType,
  SensoryRowType,
} from "../types/NCS_EMG";

type ClinRow = {
  qualifier: "" | "Consistent with" | "Suggestive of" | "Cannot exclude";
  text: string;
};

const QUALIFIERS: ClinRow["qualifier"][] = [
  "Consistent with",
  "Suggestive of",
  "Cannot exclude",
];

const sensoryAmpKey = "Amp(uV)";
const PANEL_WIDTH = 560;
const PANEL_MARGIN = 16;

const hasFilledValue = (value: unknown) =>
  typeof value === "string" ? value.trim().length > 0 : Boolean(value);

const countFilledRows = (rows: Array<Record<string, string>>) =>
  rows.filter((row) => Object.values(row).some(hasFilledValue)).length;

const Dashboard: React.FC = () => {
  const [patient, setPatient] = useState({ sex: "", age: "", note: "" });
  const [motorRows, setMotorRows] = useState<MotorRowType[]>([]);
  const [sensoryRows, setSensoryRows] = useState<SensoryRowType[]>([]);
  const [fwaveRows, setFwaveRows] = useState<FWaveRowType[]>([]);
  const [hreflexRows, setHReflexRows] = useState<HReflexRowType[]>([]);
  const [emgRows, setEmgRows] = useState<EMGRowType[]>([]);
  const [motorNerves, setMotorNerves] = useState<string[]>([]);
  const [sensoryNerves, setSensoryNerves] = useState<string[]>([]);
  const [recSites, setRecSites] = useState<string[]>([]);
  const [stimSites, setStimSites] = useState<string[]>([]);
  const [findings, setFindings] = useState<string[]>([]);
  const [showInterpretation, setShowInterpretation] = useState(false);
  const [username, setUsername] = useState("");
  const [panelPosition, setPanelPosition] = useState({ x: PANEL_MARGIN, y: PANEL_MARGIN });
  const [clinRows, setClinRows] = useState<ClinRow[]>([
    { qualifier: "", text: "" },
    { qualifier: "", text: "" },
  ]);
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);

  const sides = ["Rt.", "Lt."];

  const clampPanelPosition = (x: number, y: number) => {
    const panelWidth = Math.min(PANEL_WIDTH, window.innerWidth - PANEL_MARGIN * 2);
    const maxX = Math.max(PANEL_MARGIN, window.innerWidth - panelWidth - PANEL_MARGIN);
    const maxY = Math.max(PANEL_MARGIN, window.innerHeight - 180);

    return {
      x: Math.min(Math.max(PANEL_MARGIN, x), maxX),
      y: Math.min(Math.max(PANEL_MARGIN, y), maxY),
    };
  };

  useEffect(() => {
    if (tokenExpired()) {
      alert("Login required.");
      logout(true);
      window.location.href = "/login";
      return;
    }

    startExpiryTimer();

    const token = getToken();
    if (token) {
      try {
        const payload = decodePayload(token);
        setUsername(payload.username || payload.sub || "");
      } catch {
        // noop
      }
    }

    return () => {
      const win = window as Window & { __expiryTimer?: number };
      if (win.__expiryTimer) {
        clearTimeout(win.__expiryTimer);
      }
    };
  }, []);

  useEffect(() => {
    setMotorNerves(extractMotorNerves());
    setSensoryNerves(extractSensoryNerves());
    setRecSites(extractRecSites());
    setStimSites(extractStimSitesFlat());
  }, []);

  useEffect(() => {
    if (!showInterpretation) return;

    setPanelPosition((prev) => {
      const panelWidth = Math.min(PANEL_WIDTH, window.innerWidth - PANEL_MARGIN * 2);
      const nextX =
        prev.x === PANEL_MARGIN && prev.y === PANEL_MARGIN
          ? window.innerWidth - panelWidth - PANEL_MARGIN
          : prev.x;
      return clampPanelPosition(nextX, prev.y);
    });
  }, [showInterpretation]);

  useEffect(() => {
    if (!showInterpretation) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (!dragOffsetRef.current) return;

      const next = clampPanelPosition(
        event.clientX - dragOffsetRef.current.x,
        event.clientY - dragOffsetRef.current.y
      );
      setPanelPosition(next);
    };

    const stopDragging = () => {
      dragOffsetRef.current = null;
      document.body.style.userSelect = "";
    };

    const handleResize = () => {
      setPanelPosition((prev) => clampPanelPosition(prev.x, prev.y));
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("resize", handleResize);
      document.body.style.userSelect = "";
    };
  }, [showInterpretation]);

  const handleParsedData = (data: {
    patient?: { sex?: string; age?: string | number };
    motor?: MotorRowType[];
    sensory?: SensoryRowType[];
    fwave?: FWaveRowType[];
    hreflex?: HReflexRowType[];
    emg?: EMGRowType[];
  }) => {
    if (data.patient) {
      setPatient((prev) => ({
        ...prev,
        sex: data.patient?.sex || prev.sex,
        age:
          data.patient?.age !== undefined &&
          data.patient?.age !== null &&
          String(data.patient.age).trim() !== ""
            ? String(data.patient.age)
            : prev.age,
      }));
    }
    if (data.motor) setMotorRows(data.motor);
    if (data.sensory) setSensoryRows(data.sensory);
    if (data.fwave) setFwaveRows(data.fwave);
    if (data.hreflex) setHReflexRows(data.hreflex);
    if (data.emg) setEmgRows(data.emg);
  };

  const handleGenerateInterpretation = async () => {
    try {
      const response = await generateFindings({
        fwaveRows,
        motorRows,
        sensoryRows,
        hreflexRows,
        emgRows,
      });
      setFindings(response.data);
      setShowInterpretation(true);
    } catch (error) {
      console.error("generateFindings failed:", error);
      alert("Failed to generate interpretation.");
    }
  };

  const buildExamContext = () => {
    const context: any[] = [];

    motorRows.forEach((row) =>
      context.push({
        type: "motor",
        nerve: row.Nerve,
        side: row.Side,
        stimSite: row.Stim,
        recSite: row.Rec,
        meas: {
          lat: row["Lat(ms)"],
          amp: row["Amp(mV)"],
          cv: row["CV(m/s)"],
        },
        ref: row.Ref,
      })
    );

    sensoryRows.forEach((row) =>
      context.push({
        type: "sensory",
        nerve: row.Nerve,
        side: row.Side,
        stimSite: row.Stim,
        recSite: row.Rec,
        meas: {
          lat: row["Lat(ms)"],
          amp: row[sensoryAmpKey],
          cv: row["CV(m/s)"],
        },
        ref: row.Ref,
      })
    );

    fwaveRows.forEach((row) =>
      context.push({
        type: "fwave",
        nerve: row.Nerve,
        side: row.Side,
        meas: { lat: row["Lat(ms)"] },
        ref: row.Ref,
      })
    );

    hreflexRows.forEach((row) =>
      context.push({
        type: "hreflex",
        nerve: "Tibial",
        side: row.Side,
        meas: { lat: row["Lat(ms)"] },
        ref: row.Ref,
      })
    );

    emgRows.forEach((row) =>
      context.push({ type: "emg", muscle: row.Muscle, side: row.Side })
    );

    return context;
  };

  const clinicianOpinionString = useMemo(() => {
    return clinRows
      .map((row, index) => {
        const head = row.qualifier ? `${row.qualifier} ` : "";
        const body = row.text.trim();
        if (!head && !body) return "";
        return `${index + 1}. ${head}${body}`;
      })
      .filter(Boolean)
      .join("\n");
  }, [clinRows]);

  const examContext = useMemo(
    () => buildExamContext(),
    [motorRows, sensoryRows, fwaveRows, hreflexRows, emgRows]
  );

  const summaryItems = useMemo(
    () => [
      { label: "Motor", value: countFilledRows(motorRows) },
      { label: "Sensory", value: countFilledRows(sensoryRows) },
      { label: "F-wave", value: countFilledRows(fwaveRows) },
      { label: "H-reflex", value: countFilledRows(hreflexRows) },
      { label: "EMG", value: countFilledRows(emgRows) },
    ],
    [motorRows, sensoryRows, fwaveRows, hreflexRows, emgRows]
  );

  const updateClinRow = (index: number, patch: Partial<ClinRow>) => {
    setClinRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const addClinRow = () =>
    setClinRows((prev) => [...prev, { qualifier: "", text: "" }]);

  const removeClinRow = (index: number) =>
    setClinRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));

  const handlePanelDragStart = (event: React.MouseEvent<HTMLDivElement>) => {
    dragOffsetRef.current = {
      x: event.clientX - panelPosition.x,
      y: event.clientY - panelPosition.y,
    };
    document.body.style.userSelect = "none";
  };

  return (
    <div className="app-shell">
      <div className="dashboard-shell">
        <div className="dashboard-topbar">
          <button
            type="button"
            className="app-button secondary small"
            onClick={() => {
              window.location.href = "/ref-values";
            }}
          >
            Reference Values
          </button>
          {username && <span className="user-chip">{username}</span>}
          <AccountActions />
        </div>

        <header className="dashboard-hero">
          <div className="dashboard-hero-copy">
            <h1 className="dashboard-title">NCS/EMG AI Interpreter</h1>
            <div className="hero-meta">
              {summaryItems.map((item) => (
                <span key={item.label} className="hero-pill">
                  {item.label} {item.value}
                </span>
              ))}
            </div>
          </div>
        </header>

        <div className="dashboard-grid">
          <main className="dashboard-main">
            <div className="dashboard-main-stack">
              <FileUploader onParsed={handleParsedData} />
              <PatientInfo patient={patient} setPatient={setPatient} />

              <MotorTable
                rows={motorRows}
                setRows={setMotorRows}
                nerveOptions={motorNerves}
                recOptions={recSites}
                stimOptions={stimSites}
                sideOptions={sides}
              />

              <SensoryTable
                rows={sensoryRows}
                setRows={setSensoryRows}
                nerveOptions={sensoryNerves}
                recOptions={recSites}
                stimOptions={stimSites}
                sideOptions={sides}
              />

              <FWaveTable
                rows={fwaveRows}
                setRows={setFwaveRows}
                nerveOptions={motorNerves}
                recOptions={recSites}
                stimOptions={stimSites}
                sideOptions={sides}
              />

              <HReflexTable rows={hreflexRows} setRows={setHReflexRows} />
              <EMGPanel rows={emgRows} setRows={setEmgRows} sideOptions={sides} />

              <div className="dashboard-actions">
                <AutoFillNotes
                  motorRows={motorRows}
                  sensoryRows={sensoryRows}
                  fwaveRows={fwaveRows}
                  hreflexRows={hreflexRows}
                  emgRows={emgRows}
                  age={patient.age}
                  onUpdateNotes={(notes) => {
                    setMotorRows((prev) =>
                      prev.map((row, index) => ({ ...row, Note: notes.motor[index] || "" }))
                    );
                    setSensoryRows((prev) =>
                      prev.map((row, index) => ({
                        ...row,
                        Note: notes.sensory[index] || "",
                      }))
                    );
                    setFwaveRows((prev) =>
                      prev.map((row, index) => ({ ...row, Note: notes.fwave[index] || "" }))
                    );
                    setHReflexRows((prev) =>
                      prev.map((row, index) => ({
                        ...row,
                        Note: notes.hreflex[index] || "",
                      }))
                    );
                    setEmgRows((prev) =>
                      prev.map((row, index) => ({ ...row, Note: notes.emg[index] || "" }))
                    );
                  }}
                />

                <button className="app-button primary" onClick={handleGenerateInterpretation}>
                  Generate Interpretation
                </button>
              </div>

              <section className="surface-card opinion-card">
                <div className="card-header">
                  <h2 className="card-title">Clinician&apos;s Opinion</h2>
                </div>

                <div className="opinion-list">
                  {clinRows.map((row, index) => (
                    <div key={index} className="opinion-row">
                      <div className="opinion-index">{index + 1}.</div>

                      <select
                        value={row.qualifier}
                        onChange={(event) =>
                          updateClinRow(index, {
                            qualifier: (event.target.value as ClinRow["qualifier"]) || "",
                          })
                        }
                        className="app-select"
                      >
                        <option value=""></option>
                        {QUALIFIERS.map((qualifier) => (
                          <option key={qualifier} value={qualifier}>
                            {qualifier}
                          </option>
                        ))}
                      </select>

                      <input
                        type="text"
                        className="app-input"
                        value={row.text}
                        onChange={(event) => updateClinRow(index, { text: event.target.value })}
                        placeholder="Enter your impression"
                      />

                      <button
                        type="button"
                        onClick={() => removeClinRow(index)}
                        title="Delete row"
                        className="app-button subtle icon"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                <div className="table-controls">
                  <button type="button" onClick={addClinRow} className="app-button secondary small">
                    + Add Row
                  </button>
                </div>
              </section>
            </div>
          </main>
        </div>

        {showInterpretation && (
          <div
            className="panel-overlay"
            style={{
              left: `${panelPosition.x}px`,
              top: `${panelPosition.y}px`,
            }}
          >
            <div className="panel-sheet">
              <div className="panel-window-header" onMouseDown={handlePanelDragStart}>
                <button
                  type="button"
                  className="app-button subtle small"
                  onClick={() => setShowInterpretation(false)}
                >
                  Close
                </button>
              </div>
              <InterpretationPanel
                findings={findings}
                patient={patient}
                patientNote={patient.note}
                clinicianOpinion={clinicianOpinionString}
                context={examContext}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
